import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ActivityAsyncAdapter } from './activity.adapter';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { IInvokeDataBuildToolJobActivity, InvokeDataBuildToolJobActivity, ModelConstants, DataBuildToolJobLinkedService, ReferenceAdapter } from 'shell-models-lib';
import { ValueAdapter } from '../value.adapter';
import { getFabricLsForParameterizedConnections } from '../utils';
import { IInvokeDataBuildToolJobActivityContext } from 'app/interfaces/copilot.pipeline.action.types';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(InvokeDataBuildToolJobActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        linkedServiceName: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            dataBuildToolJobId: '*',
            workspaceId: '*',
            operation: '*',
            select: '*',
            exclude: '*',
            fullRefresh: '*',
            failFast: '*',
            threads: '*',
            selectorName: '*'
        }
    }
})
export class InvokeDataBuildToolJobActivityAdapter extends ActivityAsyncAdapter<InvokeDataBuildToolJobActivity, IInvokeDataBuildToolJobActivity, IInvokeDataBuildToolJobActivityContext> {
    public override async toFabricCopilotContext(activity: InvokeDataBuildToolJobActivity): Promise<IInvokeDataBuildToolJobActivityContext> {
        if (activity) {
            const activityContext: IInvokeDataBuildToolJobActivityContext = await super.toFabricCopilotContext(activity);
            activityContext.DataBuildToolJobId = ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.DATABUILDTOOLJOBID, true);
            activityContext.WorkspaceId = ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.WORKSPACEID, true);
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IInvokeDataBuildToolJobActivityContext,
        model?: InvokeDataBuildToolJobActivity
    ): Promise<InvokeDataBuildToolJobActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            ValueAdapter.toModel(model, InvokeDataBuildToolJobActivity.DATABUILDTOOLJOBID, context.DataBuildToolJobId);
            ValueAdapter.toModel(model, InvokeDataBuildToolJobActivity.WORKSPACEID, context.WorkspaceId);
        }
        return model;
    }

    protected async _toResource(activity: InvokeDataBuildToolJobActivity): Promise<IInvokeDataBuildToolJobActivity> {
        const modelDataBuildToolJobActivity = super._getCommonActivityProperties(
            activity, Metamodel.modelFor(InvokeDataBuildToolJobActivity).name) as IInvokeDataBuildToolJobActivity;

        modelDataBuildToolJobActivity.typeProperties = {
            dataBuildToolJobId: ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.DATABUILDTOOLJOBID),
            workspaceId: ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.WORKSPACEID),
            operation: ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.OPERATION),  
            select: ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.SELECT),
            exclude: ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.EXCLUDE),
            fullRefresh: ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.FULLREFRESH),
            failFast: ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.FAILFAST),
            threads: ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.THREADS),
            selectorName: ValueAdapter.toResource(activity, InvokeDataBuildToolJobActivity.SELECTORNAME)
        };

        modelDataBuildToolJobActivity.linkedServiceName = this._toLinkedServiceReferenceModel(activity.dataBuildToolJobLinkedService,
            activity.linkedServiceParameters);

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelDataBuildToolJobActivity, parsed);
        }

        return modelDataBuildToolJobActivity;
    }

    protected async _toModel(modelActivity: IInvokeDataBuildToolJobActivity): Promise<InvokeDataBuildToolJobActivity> {
        const activity: InvokeDataBuildToolJobActivity = InvokeDataBuildToolJobActivity.MODEL.create<InvokeDataBuildToolJobActivity>(modelActivity.name, modelActivity.description);
        this._updatedCommonProperties(modelActivity, activity);

        ValueAdapter.toModel(activity, InvokeDataBuildToolJobActivity.DATABUILDTOOLJOBID, modelActivity.typeProperties.dataBuildToolJobId);
        ValueAdapter.toModel(activity, InvokeDataBuildToolJobActivity.WORKSPACEID, modelActivity.typeProperties.workspaceId);
        ValueAdapter.toModel(activity, InvokeDataBuildToolJobActivity.OPERATION, modelActivity.typeProperties.operation);    
        ValueAdapter.toModel(activity, InvokeDataBuildToolJobActivity.SELECT, modelActivity.typeProperties.select);
        ValueAdapter.toModel(activity, InvokeDataBuildToolJobActivity.EXCLUDE, modelActivity.typeProperties.exclude);
        ValueAdapter.toModel(activity, InvokeDataBuildToolJobActivity.FULLREFRESH, modelActivity.typeProperties.fullRefresh);
        ValueAdapter.toModel(activity, InvokeDataBuildToolJobActivity.FAILFAST, modelActivity.typeProperties.failFast);
        ValueAdapter.toModel(activity, InvokeDataBuildToolJobActivity.THREADS, modelActivity.typeProperties.threads);
        ValueAdapter.toModel(activity, InvokeDataBuildToolJobActivity.SELECTORNAME, modelActivity.typeProperties.selectorName);
        this._setDataBuildToolJobConnection(activity, modelActivity);

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, InvokeDataBuildToolJobActivity.MODEL));
        return activity;
    }

    private _setDataBuildToolJobConnection(activity: InvokeDataBuildToolJobActivity, modelActivity: IInvokeDataBuildToolJobActivity): void {
        if (modelActivity.linkedServiceName !== undefined) {
            activity.dataBuildToolJobLinkedService = getFabricLsForParameterizedConnections(
                modelActivity.linkedServiceName,
                ModelConstants.dataBuildToolJobLinkedService,
                activity);

            if (!activity.dataBuildToolJobLinkedService) {
                activity.dataBuildToolJobLinkedService = ReferenceAdapter.toProxyModel<DataBuildToolJobLinkedService>(Metamodel.modelFor(DataBuildToolJobLinkedService),
                    modelActivity.linkedServiceName,
                    this.context);
            }
        }
    }
}
